<template>
  <div class="card-editor-container">
    <form @submit.prevent="handleSubmit">
      <div class="form-group">
        <img :src="editedCard.image" alt="Card Image" @click="showMaterialPicker = !showMaterialPicker" v-if="editedCard.image" />
        <label for="image">{{ $t('cardEditor.imageUrl') }}</label>
        <input type="text" id="image" v-model="editedCard.image" @click="showMaterialPicker = !showMaterialPicker" />
      </div>

      <div class="form-group">
        <label for="name">{{ $t('cardEditor.name') }}</label>
        <input type="text" id="name" v-model="editedCard.name" required />
      </div>

      <div class="form-group">
        <label for="description">{{ $t('cardEditor.description') }}</label>
        <textarea id="description" v-model="editedCard.description" rows="4"></textarea>
      </div>

      <div class="form-group">
        <label for="description">{{ $t('cardEditor.leadsTo') }}</label>
        <ActivityPicker :routeName="computedRouteName" :stream="editedCard.target" @conformedRouteSelected="handleSelectedRoute" />
        <input
          type="text"
          style="margin-top: 6px"
          v-model="editedCard.activity.property.meetLink"
          :placeholder="$t('cardEditor.activityLink')"
          v-if="editedCard.activity?.property?.meetLink && typeof editedCard.activity.property.meetLink === 'string' && editedCard.activity.property.meetLink.startsWith('http')" />
      </div>

      <div class="form-group live-timeline-checkbox" v-if="showFeedPreview">
        <label>
          <input type="checkbox" v-model="useTimelinePreview" class="styled-checkbox" />
          {{ $t('cardEditor.useTimelinePreview') }}
        </label>
      </div>
      <div class="form-group live-timeline-checkbox" v-if="showLivePreview">
        <label>
          <input type="checkbox" v-model="useLivePreview" class="styled-checkbox" />
          {{ $t('cardEditor.useLivePreview') }}
        </label>
      </div>

      <div class="button-group">
        <button type="button" class="cancel-button" @click="$emit('cancel')">{{ $t('cardEditor.cancel') }}</button>
        <button type="submit" class="save-button">{{ $t('cardEditor.save') }}</button>
      </div>

      <div class="form-group" v-if="$store.state.template.multilanguagesConfig">
        <label>Localization JSON</label>
        <v-jsoneditor v-model="editedCard.activity.property.localized" :options="{ mode: 'code', modes: ['code', 'tree', 'form', 'text'] }" :plus="true" :height="'300px'" />
      </div>
    </form>

    <MaterialPicker class="products-banner-materialPicker" :currentDataImages="[editedCard.image]" @onSelectImage="editedCard.image = $event.src" @close="showMaterialPicker = false" v-if="showMaterialPicker" />
  </div>
</template>

<script>
import ActivityPicker from '../../components/general/ActivityPicker.vue';
import MaterialPicker from '../../components/general/MaterialPicker.vue';
import VJsoneditor from 'v-jsoneditor';

export default {
  name: 'CardEditor',

  components: {
    ActivityPicker,
    MaterialPicker,
    VJsoneditor,
  },

  props: {
    card: {
      type: Object,
      required: true,
    },
  },

  data() {
    // Extract timeline_id from meetLink.params.stream if available
    let timelineId = null;
    let meetLink = this.card?.activity?.property?.meetLink;
    if (typeof meetLink === 'string') {
      try {
        meetLink = JSON.parse(meetLink);
      } catch (e) {
        meetLink = null;
      }
    }
    if (meetLink && meetLink.params && meetLink.params.stream) {
      timelineId = meetLink.params.stream;
    }
    return {
      showMaterialPicker: false,
      editedCard: { ...this.card },
      useTimelinePreview: this.card?.activity?.property?.useTimelinePreview || false,
      timelineId: timelineId,
    };
  },

  watch: {
    card(newCard) {
      this.editedCard = { ...newCard };
      // Sync useTimelinePreview from card property
      this.useTimelinePreview = newCard?.activity?.property?.useTimelinePreview || false;
      // Sync timelineId from meetLink.params.stream
      let meetLink = newCard?.activity?.property?.meetLink;
      if (typeof meetLink === 'string') {
        try {
          meetLink = JSON.parse(meetLink);
        } catch (e) {
          meetLink = null;
        }
      }
      if (meetLink && meetLink.params && meetLink.params.stream) {
        this.timelineId = meetLink.params.stream;
      } else {
        this.timelineId = null;
      }
    },
    useTimelinePreview(newVal) {
      if (this.editedCard.activity && this.editedCard.activity.property) {
        this.editedCard.activity.property.useTimelinePreview = newVal;
      }
    },
  },

  computed: {
    computedRouteName() {
      return this.editedCard.url && this.editedCard.url.startsWith('http') ? 'url' : this.editedCard.url;
    },
    showFeedPreview() {
      // Try to parse meetLink if it's a string
      let meetLink = this.editedCard.activity?.property?.meetLink;
      if (typeof meetLink === 'string') {
        try {
          meetLink = JSON.parse(meetLink);
        } catch (e) {
          // Not JSON, ignore
        }
      }
      return meetLink && meetLink.name === 'Timeline';
    },
    useLivePreview: {
      get() {
        let meetLink = this.editedCard.activity?.property?.meetLink;
        if (typeof meetLink === 'string') {
          try {
            meetLink = JSON.parse(meetLink);
          } catch (e) {
            // Not JSON, ignore
          }
        }
        return meetLink && meetLink.name === 'Live' && !!this.editedCard.activity?.property?.useLivePreview;
      },
      set(val) {
        if (this.editedCard.activity && this.editedCard.activity.property) {
          this.editedCard.activity.property.useLivePreview = val;
        }
      },
    },
    showLivePreview() {
      let meetLink = this.editedCard.activity?.property?.meetLink;
      if (typeof meetLink === 'string') {
        try {
          meetLink = JSON.parse(meetLink);
        } catch (e) {
          // Not JSON, ignore
        }
      }
      return meetLink && meetLink.name === 'Live';
    },
  },

  methods: {
    handleSelectedRoute(route) {
      //console.log('Selected route:', route);
      this.editedCard.activity.property.meetLink = route;
    },

    handleSubmit() {
      // Ensure useTimelinePreview is saved under activity.property
      if (this.editedCard.activity && this.editedCard.activity.property) {
        if (!this.editedCard.activity.property.localized) {
          this.editedCard.activity.property.localized = {
            pt: {
              name: this.editedCard.name,
              description: this.editedCard.description,
            },
          };
        }

        this.editedCard.activity.property.useTimelinePreview = this.useTimelinePreview;
        // Set timeline_id from meetLink.params.stream if available
        let meetLink = this.editedCard.activity.property.meetLink;
        if (typeof meetLink === 'string') {
          try {
            meetLink = JSON.parse(meetLink);
          } catch (e) {
            meetLink = null;
          }
        }
        if (meetLink && meetLink.params && meetLink.params.stream) {
          this.editedCard.activity.property.timeline_id = meetLink.params.stream;
        } else {
          this.editedCard.activity.property.timeline_id = null;
        }
      }
      this.$emit('save', this.editedCard);
    },
  },
};
</script>

<style scoped lang="scss">
.card-editor-container {
  background-color: #dbdbdb;
  color: #000;
  padding: 16px;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
  max-height: 100dvh;

  overflow-y: auto;
}

h2 {
  color: #000;
  margin-bottom: 20px;
  text-align: center;
}

.form-group {
  margin-bottom: 16px;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin-bottom: 10px;
    cursor: pointer;
  }

  label {
    display: block;
    margin-bottom: 6px;
  }

  input,
  textarea {
    width: 100%;
    padding: 10px;
    background-color: transparent;
    border: 1px solid #444;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #666;
    }
  }
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;

  button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }

  .cancel-button {
    background-color: transparent;
    color: #202020;
    border: 1px solid #555;

    &:hover {
      background-color: #666;
      color: white;
    }
  }

  .save-button {
    background-color: #007bff;
    color: white;

    &:hover {
      background-color: #0069d9;
    }
  }
}
// Checkbox styling
.live-timeline-checkbox {
  display: flex;
  align-items: center;
  background: transparent;

  label {
    display: flex;
    align-items: center;
    font-size: 14px;
    background: transparent;
  }
  .styled-checkbox {
    width: 18px;
    height: 18px;
    accent-color: #444;
    margin-right: 8px;
    border-radius: 4px;
    border: 1px solid #444;
    cursor: pointer;
    background: transparent !important;
    box-shadow: none !important;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
    position: relative;
    display: inline-block;
    vertical-align: middle;
    transition: border-color 0.2s;
  }
  .styled-checkbox:checked {
    border-color: #444;
  }
  .styled-checkbox:checked::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 44%; /* was 50% */
    width: 6px;
    height: 12px;
    border: solid #444;
    border-width: 0 2px 2px 0;
    transform: translate(-50%, -50%) rotate(45deg);
    pointer-events: none;
    background: none;
    z-index: 1;
  }
  .styled-checkbox:focus {
    outline: none;
    box-shadow: 0 0 0 2px #444;
    background: transparent !important;
  }
}
</style>
